import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { IAppState } from '../../../common/state';
import { AB_COWORKING_POPUP_EXPERIMENT } from '../../../constants/coworkingsExperiment';
import { EPosition } from '../../../filters/components/filters/filters';
import { isAbUseExperimentGroup } from '../../../utils/experiments/isAbUseExperimentGroup';
import { isOfficeRentSearch } from '../../../utils/isOfficeRentSearch';

import { OFFERS_SCROLLED_TO_TOP } from './coworkingAdModalMini';

const LOCAL_STORAGE_COWORKING_POPUP_ITEM = 'isCoworkingPopupClosed';

// ACTION TYPES
export const COWORKING_POPUP_OPENED = 'modals/coworking_popup/MODAL_OPENED';
export const COWORKING_POPUP_CLOSED = 'modals/coworking_popup/MODAL_CLOSED';
export const SET_FILTERS_POSITION = 'setFiltersPosition';

// ACTIONS
export interface ICoworkingPopupOpened {
  type: typeof COWORKING_POPUP_OPENED;
}
export interface ICoworkingPopupClosed {
  type: typeof COWORKING_POPUP_CLOSED;
  closeForever?: boolean;
}

export interface ISetFiltersPosition {
  type: typeof SET_FILTERS_POSITION;
  position: EPosition;
}

export type TCoworkingPopupActions = ICoworkingPopupOpened | ICoworkingPopupClosed | ISetFiltersPosition;

export const openCoworkingPopup = (): ICoworkingPopupOpened => ({ type: COWORKING_POPUP_OPENED });
export const closedCoworkingPopup = (closeForever: boolean = true): ICoworkingPopupClosed => ({
  type: COWORKING_POPUP_CLOSED,
  closeForever,
});
export const setFiltersPosition = (position: EPosition) => ({
  type: SET_FILTERS_POSITION,
  position,
});

// SAGAS
export function* handleOffersScrolledToTop() {
  const {
    logger,
    filters: { jsonQuery },
  } = (yield select()) as IAppState;

  try {
    const isCoworkingPopupClosed = localStorage.getItem(LOCAL_STORAGE_COWORKING_POPUP_ITEM);

    if (isOfficeRentSearch(jsonQuery) && isCoworkingPopupClosed !== 'true') {
      yield put(openCoworkingPopup());
    }
  } catch (error) {
    logger.warning(error, {
      description: 'Попытка обращения к localStorage',
      domain: 'handleOffersScrolledToTop',
      message: 'Ошибка при чтении данных из localStorage',
    });
  }
}

export function* handleCoworkingPopupClosed({ closeForever }: ICoworkingPopupClosed) {
  const { logger } = (yield select()) as IAppState;

  if (!closeForever) {
    return;
  }

  try {
    localStorage.setItem(LOCAL_STORAGE_COWORKING_POPUP_ITEM, 'true');
  } catch (error) {
    logger.warning(error, {
      description: 'Попытка обращения к localStorage',
      domain: 'handleOffersScrolledToTop',
      message: 'Ошибка призаписи данных в localStorage',
    });
  }
}

export function* handleSetFiltersPosition(action: ISetFiltersPosition) {
  const { position } = action;
  const {
    coworkingExperiments: { coworkingPopup },
  } = (yield select()) as IAppState;

  if (position === EPosition.default && coworkingPopup.isOpen) {
    yield put(closedCoworkingPopup(false));

    return;
  }
}

export function* coworkingPopupSagas() {
  const { abUseExperiments, isCoworkingExperimentsEnabled } = (yield select()) as IAppState;

  const isExperimentEnabled: boolean = yield call(
    isAbUseExperimentGroup,
    abUseExperiments,
    AB_COWORKING_POPUP_EXPERIMENT,
    'D',
  );

  if (!isCoworkingExperimentsEnabled || !isExperimentEnabled) {
    return;
  }

  yield all([
    takeLatest(OFFERS_SCROLLED_TO_TOP, handleOffersScrolledToTop),
    takeLatest(COWORKING_POPUP_CLOSED, handleCoworkingPopupClosed),
    takeLatest(SET_FILTERS_POSITION, handleSetFiltersPosition),
  ]);
}

// REDUCER
export interface ICoworkingPopup {
  isOpen: boolean;
  filtersPosition: EPosition;
}

export const initialState = {
  isOpen: false,
  filtersPosition: EPosition.default,
};

export const coworkingPopupReducer = (
  state: ICoworkingPopup = initialState,
  action: TCoworkingPopupActions,
): ICoworkingPopup => {
  switch (action.type) {
    case COWORKING_POPUP_OPENED:
      return {
        ...state,
        isOpen: true,
      };

    case COWORKING_POPUP_CLOSED:
      return {
        ...state,
        isOpen: false,
      };

    case SET_FILTERS_POSITION:
      return {
        ...state,
        filtersPosition: action.position,
      };

    default:
      return state;
  }
};
